<template>
  <client-only>
    <article :class="{ placeholder: placeholder }" class="picture">

        <div class="img" :style="imgStyle">
          <span v-if="picture.is_hd" class="hd"> HD </span>
          <span v-if="picture.length" class="length">
            {{ pictureLengthFormatted }}
          </span>
        </div>
        
    </article>
  </client-only>
</template>

<script setup lang="ts">
import { Post } from "~/types/Post";

/* Props */
interface _Props {
  placeholder?: boolean;
  picture?: Post;
}

const { placeholder, picture } = withDefaults(defineProps<_Props>(), {
  placeholder: false,
});

if (picture === undefined && !placeholder)
  console.error(`INVALID PICTURE: ${picture}`);

/* Computed */
const pictureLengthFormatted = computed(() => {
  if (picture.length === null) return null;

  const matchers = new Date(picture.length * 1000)
    .toUTCString()
    .match(/(\d\d:\d\d:\d\d)/);
  if (matchers === null) return null;

  const videoDuration = matchers[0];
  if (videoDuration.startsWith("00:"))
    return videoDuration.substr(3, videoDuration.length);
  else return videoDuration;
});

const imgStyle = computed(() => {
  let image = { "large": "" };

  if (picture.image_path){
      image = picture.image_path;
  } else {
      image = picture.public_thumbnail_image_path;
  }

  let w = picture.dimentions.width;
  let h = picture.dimentions.height;

  if (!w || !h) {
    w = 1; h = 1; // Avoid division by zero
  }

  return {
    width: "100%",
    aspectRatio: w + " / " + h,
    backgroundImage: `url('${image["large"]}')`,
    backgroundPosition: "center",
  };
});
</script>

<style scoped lang="scss">
.picture {
  flex-grow: 0;
  flex-basis: 0;
  transition: width 0.5s ease;
  width: 100%;

  .img {
    position: relative;
    background-size: contain;
    background-position-x: center;
    background-position-y: center;
    background-repeat: no-repeat;
    margin-bottom: 0.5em;
    transition: height 0.5s ease;
    max-height: 90vh;

    img {
      width: 100%;
      visibility: hidden;
    }

    span.hd {
      position: absolute;
      left: 5px;
      top: 5px;
      padding: 3px;
      font-weight: bold;
      border-radius: 2px;
      background-color: var(--pallete_dark_color);
      color: #ffffff;
    }

    span.length {
      position: absolute;
      right: 5px;
      bottom: 5px;
      padding: 3px;
      border-radius: 2px;
      font-size: 13px;
      background-color: var(--pallete_dark_color);
      color: #ffffff;
    }

    .preview-wrapper {
      opacity: 0;
      transition: opacity 0.3s linear;

      &.active {
        opacity: 1;
      }
    }

    .preview-wrapper.active {
      .preview {
        background-color: var(--pallete_dark_color);
        position: absolute;
        overflow: hidden;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        video {
          /*object-fit: cover;*/
          overflow: hidden;
          min-width: 100%;
          min-height: 100%;
        }
      }

      .preview-unavailable {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.placeholder {
    .img {
      background-color: rgba(128, 128, 128, 0.25);
    }

    .text {
      .description {
        background-color: rgba(128, 128, 128, 0.25);
        height: 1em;
      }

      .name {
        background-color: rgba(128, 128, 128, 0.25);
        height: 1em;
      }
    }
  }

  .locked {
    z-index: 1;

    &:after {
      content: "";
      background: var(--background);
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      opacity: 0.4;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      position: absolute;
      z-index: -1;
    }

    img {
      width: 100%;
      visibility: hidden;
      max-height: 95vh;
    }

    .lock {
      display: block;
      width: 100%;
      height: 100%;
      background-image: url("/img/lock-white.png");
      background-size: 100px;
      background-repeat: no-repeat;
      background-position: center;
    }
  }
}
</style>
